import { memo, useCallback, useEffect, useState } from 'react'
import { CalendarClock, CircleX, Newspaper, Send, Siren, TriangleAlert } from 'lucide-react'
import { Spinner } from '../common/Spinner.js'
import { RecipientListField } from './RecipientListField.js'
import { invalidRecipients } from '../../lib/recipientList.js'
import {
  notificationsApi,
  ALERT_TYPE_LABELS,
  DAILY_REPORT_DELIVERY_LABEL,
  INFOSERVICE_ALERT_DELIVERY_LABEL,
  PROGRAM_EVENT_LABELS,
  SEVERITY_TIERS,
  type NotificationConfig,
  type NotificationConfigPatch,
  type NotificationDelivery,
  type DailyReportPreview,
  type TelegramChat,
} from '../../lib/settingsApi.js'

/**
 * Configuration des notifications (e-mail SMTP + Telegram).
 *
 * La page se lit de haut en bas en parties nommées :
 *   1. Notifications techniques — incidents d'antenne, pour l'astreinte ;
 *   2. Notifications éditoriales — vie de la grille, pour la rédaction, avec
 *      en sous-partie le rapport journalier qui prépare le lendemain ;
 *   3. Transports — socle commun à tous les envois.
 *
 * Les deux flux partagent le même serveur SMTP et le même bot Telegram mais pas
 * leurs destinataires ni leurs interrupteurs. Ce socle vit dans la quatrième
 * partie, en bas : le dupliquer dans chaque flux laisserait croire qu'il s'agit
 * de deux configurations indépendantes, et le placer en tête ferait ouvrir la
 * page sur de la plomberie plutôt que sur ce qui part réellement.
 *
 * Les secrets sont en écriture seule : le backend ne renvoie jamais le mot de
 * passe ni le jeton, seulement un booléen de présence. Un champ laissé vide
 * conserve donc le secret enregistré au lieu de l'effacer.
 */

type Busy =
  | 'save' | 'test-email' | 'test-telegram' | 'discover'
  | 'test-program-email' | 'test-program-telegram' | 'maintenance'
  | 'report-preview' | 'report-send'
  | null

const LEAD_CHOICES = [5, 10, 15, 30, 45, 60, 120]

/** Jours ISO, lundi en tête comme dans la grille. */
const REPORT_DAYS = [
  { value: 1, short: 'Lun', label: 'lundi' },
  { value: 2, short: 'Mar', label: 'mardi' },
  { value: 3, short: 'Mer', label: 'mercredi' },
  { value: 4, short: 'Jeu', label: 'jeudi' },
  { value: 5, short: 'Ven', label: 'vendredi' },
  { value: 6, short: 'Sam', label: 'samedi' },
  { value: 7, short: 'Dim', label: 'dimanche' },
]

/** Rubriques du rapport, rappelées à l'écran pour que l'opérateur sache ce qui part. */
const REPORT_CONTENT = [
  { title: 'Préparer demain', items: 'médias manquants, fiches EPG incomplètes, EPG des inédits, trous à combler, directs avec rappel de test de source, inédits, export EPG, grilles de la semaine' },
  { title: 'Publicité', items: 'campagnes placées, spots sans média, campagnes qui commencent ou se terminent' },
  { title: 'Bilan du jour', items: 'éléments non diffusés, écarts de durée, incidents d’antenne' },
  { title: 'Suite de la semaine', items: 'une ligne par jour, de J+2 à J+7' },
]

const REPORT_ERROR_LABELS: Record<string, string> = {
  SMTP_HOST_MISSING:        'Aucun serveur SMTP réglé dans Transports.',
  EMAIL_RECIPIENTS_MISSING: 'Aucune adresse e-mail enregistrée pour le rapport.',
  EMAIL_FROM_MISSING:       'Aucun expéditeur réglé dans Transports.',
  TELEGRAM_TOKEN_MISSING:   'Aucun jeton de bot Telegram enregistré.',
  TELEGRAM_CHAT_MISSING:    'Doublage Telegram coché sans conversation enregistrée.',
  NO_ACTIVE_CHANNEL:        'Aucune chaîne active.',
}

const MAINTENANCE_CHOICES = [15, 30, 60]

/** Icône par niveau : la gravité se lit à la forme autant qu'à la couleur. */
const TIER_ICONS: Record<string, typeof Siren> = {
  WARNING:  TriangleAlert,
  ERROR:    CircleX,
  CRITICAL: Siren,
}

const COOLDOWN_CHOICES = [
  { value: 0,    label: 'Aucun (chaque alerte)' },
  { value: 60,   label: '1 minute' },
  { value: 300,  label: '5 minutes' },
  { value: 900,  label: '15 minutes' },
  { value: 3600, label: '1 heure' },
]

export const NotificationSettings = memo(function NotificationSettings() {
  const [config, setConfig]         = useState<NotificationConfig | null>(null)
  const [alertCatalogue, setAlertCatalogue] = useState<Record<string, string[]>>({})
  const [tiers, setTiers] = useState<string[]>([])
  const [programEvents, setProgramEvents] = useState<string[]>([])
  const [deliveries, setDeliveries] = useState<NotificationDelivery[]>([])
  const [smtpPassword, setSmtpPassword]     = useState('')
  const [telegramToken, setTelegramToken]   = useState('')
  const [busy, setBusy]       = useState<Busy>(null)
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)
  const [discovered, setDiscovered] = useState<TelegramChat[] | null>(null)
  const [reportPreview, setReportPreview] = useState<DailyReportPreview | null>(null)
  /** Réglages modifiés depuis le dernier enregistrement : l'envoi immédiat lit la base. */
  const [dirty, setDirty] = useState(false)

  const loadHistory = useCallback(async () => {
    try {
      const { deliveries: rows } = await notificationsApi.deliveries(20)
      setDeliveries(rows)
    } catch { /* L'historique est secondaire : son échec ne bloque pas la page. */ }
  }, [])

  const load = useCallback(async () => {
    const response = await notificationsApi.get()
    setConfig(response.config)
    setAlertCatalogue(response.alertCatalogue)
    setTiers(response.tiers)
    setProgramEvents(response.programEvents)
    await loadHistory()
  }, [loadHistory])

  useEffect(() => {
    void load().catch(error => setMessage({ tone: 'error', text: error.message }))
  }, [load])

  const patch = useCallback((values: Partial<NotificationConfig>) => {
    setConfig(current => current ? { ...current, ...values } : current)
    setDirty(true)
  }, [])

  const handleSave = useCallback(async () => {
    if (!config) return
    // Le serveur refuserait tout l'enregistrement pour une seule adresse mal
    // tapée, avec un message technique : on le dit avant, en clair.
    const lists: Array<[string, string[], 'email' | 'telegram']> = [
      ['astreinte (e-mail)', config.emailTo, 'email'],
      ['astreinte (Telegram)', config.telegramChatIds, 'telegram'],
      ['rédaction (e-mail)', config.programEmailTo, 'email'],
      ['rédaction (Telegram)', config.programChatIds, 'telegram'],
      ['rapport (e-mail)', config.reportEmailTo, 'email'],
      ['rapport (Telegram)', config.reportChatIds, 'telegram'],
    ]
    for (const [where, entries, kind] of lists) {
      const invalid = invalidRecipients(entries, kind)
      if (invalid.length > 0 || entries.length > 20) {
        setMessage({
          tone: 'error',
          text: invalid.length > 0
            ? `Destinataires ${where} : ${invalid.join(', ')} invalide(s)`
            : `Destinataires ${where} : 20 au maximum`,
        })
        return
      }
    }
    setBusy('save'); setMessage(null)
    try {
      const body: NotificationConfigPatch = {
        enabled:       config.enabled,
        severityTiers: config.severityTiers,
        mutedTypes:    config.mutedTypes,
        cooldownSec:   config.cooldownSec,

        emailEnabled: config.emailEnabled,
        smtpHost:     config.smtpHost,
        smtpPort:     config.smtpPort,
        smtpSecure:   config.smtpSecure,
        smtpUser:     config.smtpUser,
        emailFrom:    config.emailFrom,
        emailTo:      config.emailTo,

        telegramEnabled: config.telegramEnabled,
        telegramChatIds: config.telegramChatIds,

        programEnabled:      config.programEnabled,
        programEvents:       config.programEvents,
        programChatIds:      config.programChatIds,
        programEmailEnabled: config.programEmailEnabled,
        programEmailTo:      config.programEmailTo,
        liveLeadMinutes:     config.liveLeadMinutes,

        reportEnabled:         config.reportEnabled,
        reportTime:            config.reportTime,
        reportDays:            config.reportDays,
        reportEmailTo:         config.reportEmailTo,
        reportTelegramEnabled: config.reportTelegramEnabled,
        reportChatIds:         config.reportChatIds,

        infoserviceAlertEnabled: config.infoserviceAlertEnabled,
      }
      // Champ vide = secret inchangé. Pour l'effacer, l'opérateur décoche le transport.
      if (smtpPassword !== '')  body.smtpPassword     = smtpPassword
      if (telegramToken !== '') body.telegramBotToken = telegramToken

      const { config: saved } = await notificationsApi.update(body)
      setConfig(saved)
      setSmtpPassword(''); setTelegramToken('')
      setDirty(false)
      setMessage({ tone: 'ok', text: 'Configuration enregistrée' })
    } catch (error) {
      setMessage({ tone: 'error', text: error instanceof Error ? error.message : String(error) })
    } finally { setBusy(null) }
  }, [config, smtpPassword, telegramToken])

  const handleTest = useCallback(async (
    transport: 'EMAIL' | 'TELEGRAM',
    stream: 'INCIDENT' | 'PROGRAM' = 'INCIDENT',
  ) => {
    const prefix = stream === 'PROGRAM' ? 'test-program-' : 'test-'
    setBusy(`${prefix}${transport === 'EMAIL' ? 'email' : 'telegram'}` as Busy)
    setMessage(null)
    try {
      await notificationsApi.test(transport, stream)
      setMessage({ tone: 'ok', text: `Message de test ${transport === 'EMAIL' ? 'e-mail' : 'Telegram'} envoyé` })
    } catch (error) {
      setMessage({ tone: 'error', text: error instanceof Error ? error.message : String(error) })
    } finally {
      setBusy(null)
      await loadHistory()
    }
  }, [loadHistory])

  const handleDiscover = useCallback(async () => {
    setBusy('discover'); setMessage(null)
    try {
      const { chats } = await notificationsApi.telegramChats()
      setDiscovered(chats)
      if (chats.length === 0) {
        setMessage({
          tone: 'error',
          text: 'Aucune conversation détectée. Ajoutez le bot au groupe, envoyez-y « /start » (un message ordinaire ne suffit pas : le mode privacy le masque au bot), puis relancez la détection.',
        })
      }
    } catch (error) {
      setMessage({ tone: 'error', text: error instanceof Error ? error.message : String(error) })
    } finally { setBusy(null) }
  }, [])

  const addChatId = useCallback((id: string, stream: 'INCIDENT' | 'PROGRAM' | 'REPORT') => {
    setConfig(current => {
      if (!current) return current
      const field = stream === 'PROGRAM' ? 'programChatIds'
        : stream === 'REPORT' ? 'reportChatIds'
        : 'telegramChatIds'
      if (current[field].includes(id)) return current
      return { ...current, [field]: [...current[field], id] }
    })
    setDirty(true)
  }, [])

  const toggleReportDay = useCallback((day: number, on: boolean) => {
    setConfig(current => {
      if (!current) return current
      const selected = new Set(current.reportDays)
      if (on) selected.add(day)
      else selected.delete(day)
      return { ...current, reportDays: [...selected].sort((a, b) => a - b) }
    })
    setDirty(true)
  }, [])

  const handleReportPreview = useCallback(async () => {
    setBusy('report-preview'); setMessage(null)
    try {
      const { report } = await notificationsApi.dailyReportPreview()
      setReportPreview(report)
    } catch (error) {
      setMessage({ tone: 'error', text: error instanceof Error ? error.message : String(error) })
    } finally { setBusy(null) }
  }, [])

  const handleReportSend = useCallback(async () => {
    setBusy('report-send'); setMessage(null)
    try {
      await notificationsApi.dailyReportSend()
      setMessage({ tone: 'ok', text: 'Rapport journalier envoyé' })
    } catch (error) {
      const raw = error instanceof Error ? error.message : String(error)
      setMessage({ tone: 'error', text: REPORT_ERROR_LABELS[raw] ?? raw })
    } finally {
      setBusy(null)
      await loadHistory()
    }
  }, [loadHistory])

  const toggleProgramEvent = useCallback((type: string, on: boolean) => {
    setConfig(current => {
      if (!current) return current
      const selected = new Set(current.programEvents)
      if (on) selected.add(type)
      else selected.delete(type)
      return { ...current, programEvents: [...selected] }
    })
    setDirty(true)
  }, [])

  const handleMaintenance = useCallback(async (minutes: number) => {
    setBusy('maintenance'); setMessage(null)
    try {
      const { maintenanceUntil } = await notificationsApi.maintenance(minutes)
      patch({ maintenanceUntil })
      setMessage({
        tone: 'ok',
        text: maintenanceUntil
          ? `Notifications silencieuses jusqu’à ${new Date(maintenanceUntil).toLocaleTimeString('fr-FR')}`
          : 'Silence levé',
      })
    } catch (error) {
      setMessage({ tone: 'error', text: error instanceof Error ? error.message : String(error) })
    } finally { setBusy(null) }
  }, [patch])

  /** `key` = `NIVEAU:TYPE` : couper la dérive en avertissement garde la dérive critique. */
  const toggleMuted = useCallback((key: string, notify: boolean) => {
    setConfig(current => {
      if (!current) return current
      const muted = new Set(current.mutedTypes)
      if (notify) muted.delete(key)
      else muted.add(key)
      return { ...current, mutedTypes: [...muted] }
    })
    setDirty(true)
  }, [])

  const toggleTier = useCallback((tier: string, on: boolean) => {
    setConfig(current => {
      if (!current) return current
      const selected = new Set(current.severityTiers)
      if (on) selected.add(tier)
      else selected.delete(tier)
      return { ...current, severityTiers: tiers.filter(entry => selected.has(entry)) }
    })
    setDirty(true)
  }, [tiers])

  const setEmailTo = useCallback((emailTo: string[]) => patch({ emailTo }), [patch])
  const setTelegramChatIds = useCallback((telegramChatIds: string[]) => patch({ telegramChatIds }), [patch])
  const setProgramChatIds = useCallback((programChatIds: string[]) => patch({ programChatIds }), [patch])
  const setProgramEmailTo = useCallback((programEmailTo: string[]) => patch({ programEmailTo }), [patch])
  const setReportEmailTo = useCallback((reportEmailTo: string[]) => patch({ reportEmailTo }), [patch])
  const setReportChatIds = useCallback((reportChatIds: string[]) => patch({ reportChatIds }), [patch])

  if (!config) {
    return <div className="settings-content--wide"><p><Spinner /> Chargement de la configuration…</p></div>
  }

  return (
    <div className="settings-content--wide">
      <div className="settings-view-header">
        <div>
          <h2 className="settings-view-title">Notifications</h2>
          <p className="settings-view-description">
            Trois parties : les incidents pour l’astreinte technique, les événements de grille pour la
            rédaction, et les rapports journaliers. Le serveur d’envoi commun aux deux flux se règle en
            bas de page.
          </p>
        </div>
        <div className="settings-view-header__actions">
          {message && (
            <span className={message.tone === 'ok' ? 'settings-saved' : 'settings-error'} role="status">
              {message.tone === 'ok' ? '✓ ' : '⚠ '}{message.text}
            </span>
          )}
          <button className="tp-button tp-button--primary" disabled={busy !== null} onClick={() => void handleSave()}>
            Sauvegarder
          </button>
        </div>
      </div>

      {/* ══ 1. Notifications techniques ═══════════════════════════════════════ */}
      <section className="settings-group settings-group--technical" aria-labelledby="notif-tech-title">
        <header className="settings-group__heading">
          <span className="settings-group__icon" aria-hidden="true"><Siren size={18} /></span>
          <div className="settings-group__heading-copy">
            <div className="settings-group__title-row">
              <h3 id="notif-tech-title" className="settings-group__title">Notifications techniques</h3>
              <span className="settings-group__tag">Astreinte</span>
            </div>
            <p className="settings-group__description">
              Incidents d’antenne destinés à l’astreinte, rangés en trois niveaux : avertissements,
              erreurs, critiques. Les alertes restent visibles dans l’interface même lorsque les envois sont
              coupés.
            </p>
          </div>
        </header>

        <div className="settings-section">
          <label className="settings-option-row settings-option-row--selectable">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={event => patch({ enabled: event.target.checked })}
            />
            <span className="settings-option-row__copy">
              <span className="settings-option-row__title">Activer les notifications techniques</span>
              <span className="settings-option-row__description">
                Interrupteur général de ce flux — coupe l’e-mail et Telegram d’un seul geste. Sans effet
                sur les notifications éditoriales.
              </span>
            </span>
          </label>

          <div className="settings-redundancy__form-grid">
            <label>
              <span>Anti-répétition</span>
              <select
                className="tp-input"
                value={config.cooldownSec}
                onChange={event => patch({ cooldownSec: Number(event.target.value) })}
              >
                {COOLDOWN_CHOICES.map(choice => (
                  <option key={choice.value} value={choice.value}>{choice.label}</option>
                ))}
              </select>
            </label>
          </div>
          <p className="settings-section__description">
            Un même défaut sur un même élément de grille n’est notifié qu’une fois par période. Un défaut sur
            un autre élément, ou le même défaut qui s’aggrave (avertissement → critique), passe immédiatement.
          </p>
        </div>

        <div className="settings-section">
          <div className="settings-section__heading">
            <h4 className="settings-section__title">Niveaux de gravité</h4>
            <p className="settings-section__description">
              Chaque niveau a son interrupteur, puis ses alertes une à une. Une même alerte peut exister à
              deux niveaux : la dérive de durée est un avertissement à 30 s et devient critique à 2 min.
            </p>
          </div>

          {config.enabled && config.severityTiers.length === 0 && (
            <p className="settings-error" role="status">
              ⚠ Aucun niveau activé : aucune notification technique ne partira.
            </p>
          )}

          <div className="settings-severity-tiers">
            {tiers.map(tier => {
              const TierIcon = TIER_ICONS[tier] ?? Siren
              const tierOn = config.severityTiers.includes(tier)
              const types = alertCatalogue[tier] ?? []
              const active = types.filter(type => !config.mutedTypes.includes(`${tier}:${type}`)).length
              const copy = SEVERITY_TIERS[tier] ?? { label: tier, description: '' }
              return (
                <fieldset
                  key={tier}
                  className={`settings-severity-tier settings-severity-tier--${tier.toLowerCase()}`}
                  data-off={tierOn ? undefined : ''}
                >
                  <legend className="settings-severity-tier__legend">
                    <label className="settings-severity-tier__switch">
                      <input
                        type="checkbox"
                        checked={tierOn}
                        onChange={event => toggleTier(tier, event.target.checked)}
                      />
                      <span className="settings-severity-tier__icon" aria-hidden="true"><TierIcon size={16} /></span>
                      <span className="settings-severity-tier__name">{copy.label}</span>
                    </label>
                    <span className="settings-severity-tier__count">
                      {tierOn ? `${active} / ${types.length} notifiée${active > 1 ? 's' : ''}` : 'Non notifié'}
                    </span>
                  </legend>
                  <p className="settings-severity-tier__description">{copy.description}</p>
                  <div className="settings-severity-tier__types">
                    {types.map(type => {
                      const key = `${tier}:${type}`
                      return (
                        <label key={key} className="settings-option-row settings-option-row--selectable">
                          <input
                            type="checkbox"
                            checked={!config.mutedTypes.includes(key)}
                            disabled={!tierOn}
                            onChange={event => toggleMuted(key, event.target.checked)}
                          />
                          <span className="settings-option-row__copy">
                            <span className="settings-option-row__title">{ALERT_TYPE_LABELS[type] ?? type}</span>
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </fieldset>
              )
            })}
          </div>
        </div>

        <div className="settings-section">
          <div className="settings-section__heading">
            <h4 className="settings-section__title">Destinataires de l’astreinte</h4>
            <p className="settings-section__description">
              Chaque transport garde son interrupteur : l’astreinte peut être jointe sur Telegram seul,
              par e-mail seul, ou les deux.
            </p>
          </div>

          {/* Une colonne par transport : l'interrupteur, ses destinataires et son
              test se lisent ensemble, au lieu d'être séparés par le bloc de
              l'autre transport. */}
          <div className="settings-recipients">
            <div className="settings-recipients__column">
              <label className="settings-option-row settings-option-row--selectable">
                <input
                  type="checkbox"
                  checked={config.emailEnabled}
                  onChange={event => patch({ emailEnabled: event.target.checked })}
                />
                <span className="settings-option-row__copy">
                  <span className="settings-option-row__title">Envoyer par e-mail</span>
                </span>
              </label>

              <RecipientListField
                label="Adresses e-mail" kind="email"
                placeholder={'astreinte@exemple.fr\ntechnicien@exemple.fr'}
                value={config.emailTo}
                onChange={setEmailTo}
              />

              <div className="settings-actions">
                <button
                  className="tp-button"
                  disabled={busy !== null || !config.smtpHost || config.emailTo.length === 0}
                  onClick={() => void handleTest('EMAIL')}
                >
                  {busy === 'test-email' ? 'Envoi…' : 'Tester l’e-mail technique'}
                </button>
              </div>
            </div>

            <div className="settings-recipients__column">
              <label className="settings-option-row settings-option-row--selectable">
                <input
                  type="checkbox"
                  checked={config.telegramEnabled}
                  onChange={event => patch({ telegramEnabled: event.target.checked })}
                />
                <span className="settings-option-row__copy">
                  <span className="settings-option-row__title">Envoyer sur Telegram</span>
                </span>
              </label>

              <RecipientListField
                label="Conversations Telegram" kind="telegram"
                placeholder={'123456789\n-1001234567890'}
                value={config.telegramChatIds}
                onChange={setTelegramChatIds}
                hint={<>Préfixe <code>-100</code> pour un groupe ; préférez « Détecter les conversations », dans <strong>Transports</strong>.</>}
              />

              <div className="settings-actions">
                <button
                  className="tp-button"
                  disabled={busy !== null || config.telegramChatIds.length === 0}
                  onClick={() => void handleTest('TELEGRAM')}
                >
                  {busy === 'test-telegram' ? 'Envoi…' : 'Tester le Telegram technique'}
                </button>
              </div>
            </div>
          </div>
          <p className="settings-section__description">
            Le test utilise la configuration <strong>enregistrée</strong> : sauvegardez avant de tester.
          </p>
        </div>
      </section>

      {/* ══ 2. Notifications éditoriales ══════════════════════════════════════ */}
      <section className="settings-group settings-group--editorial" aria-labelledby="notif-editorial-title">
        <header className="settings-group__heading">
          <span className="settings-group__icon" aria-hidden="true"><Newspaper size={18} /></span>
          <div className="settings-group__heading-copy">
            <div className="settings-group__title-row">
              <h3 id="notif-editorial-title" className="settings-group__title">Notifications éditoriales</h3>
              <span className="settings-group__tag">Rédaction</span>
            </div>
            <p className="settings-group__description">
              Vie de la grille destinée à la rédaction : lancement et fin de programme, pré-annonce des
              directs, et rapport journalier qui prépare le lendemain. Même bot Telegram et même serveur
              SMTP que le flux technique, <strong>destinataires distincts</strong>.
            </p>
          </div>
        </header>

        <div className="settings-section">
          <label className="settings-option-row settings-option-row--selectable">
            <input
              type="checkbox"
              checked={config.programEnabled}
              onChange={event => patch({ programEnabled: event.target.checked })}
            />
            <span className="settings-option-row__copy">
              <span className="settings-option-row__title">Activer les notifications éditoriales</span>
              <span className="settings-option-row__description">
                Sans effet sur les alertes techniques, qui gardent leur propre interrupteur.
              </span>
            </span>
          </label>
        </div>

        <div className="settings-section">
          <div className="settings-section__heading">
            <h4 className="settings-section__title">Événements notifiés</h4>
          </div>
          <div className="settings-notification-types">
            {programEvents.map(type => (
              <label key={type} className="settings-option-row settings-option-row--selectable">
                <input
                  type="checkbox"
                  checked={config.programEvents.includes(type)}
                  onChange={event => toggleProgramEvent(type, event.target.checked)}
                />
                <span className="settings-option-row__copy">
                  <span className="settings-option-row__title">{PROGRAM_EVENT_LABELS[type] ?? type}</span>
                </span>
              </label>
            ))}
            <label className="settings-option-row settings-option-row--selectable">
              <input
                type="checkbox"
                checked={config.infoserviceAlertEnabled}
                onChange={event => patch({ infoserviceAlertEnabled: event.target.checked })}
              />
              <span className="settings-option-row__copy">
                <span className="settings-option-row__title">{INFOSERVICE_ALERT_DELIVERY_LABEL}</span>
                <span className="settings-option-row__description">
                  À 18:00, les annonces cochées pour le lendemain sans image (elles ne passeront pas).
                  Rien n’est envoyé quand tout est prêt. Indépendant de
                  l’interrupteur des notifications éditoriales.
                </span>
              </span>
            </label>
          </div>

          <div className="settings-redundancy__form-grid">
            <label>
              <span>Pré-annonce des directs</span>
              <select
                className="tp-input"
                value={config.liveLeadMinutes}
                onChange={event => patch({ liveLeadMinutes: Number(event.target.value) })}
              >
                {LEAD_CHOICES.map(minutes => (
                  <option key={minutes} value={minutes}>{minutes} minutes avant l’antenne</option>
                ))}
              </select>
            </label>
          </div>
          <p className="settings-section__description">
            Un seul message par direct, envoyé dès l’entrée dans la fenêtre. Un direct ajouté à la grille
            moins de {config.liveLeadMinutes} minutes avant son antenne est annoncé immédiatement.
          </p>
        </div>

        <div className="settings-section">
          <div className="settings-section__heading">
            <h4 className="settings-section__title">Destinataires de la rédaction</h4>
          </div>

          {/* Telegram est le transport principal du flux éditorial, l'e-mail un
              doublage : les deux colonnes gardent le même gabarit que celles de
              l'astreinte, l'interrupteur de tête portant ici le doublage. */}
          <div className="settings-recipients">
            <div className="settings-recipients__column">
              <label className="settings-option-row settings-option-row--selectable settings-option-row--static">
                <span className="settings-option-row__copy">
                  <span className="settings-option-row__title">Telegram — canal principal</span>
                </span>
              </label>

              <RecipientListField
                label="Conversations Telegram" kind="telegram"
                placeholder={'-1009876543210'}
                value={config.programChatIds}
                onChange={setProgramChatIds}
                hint={<>« Détecter les conversations » dans <strong>Transports</strong>, puis « Ajouter à l’éditorial ».</>}
              />

              <div className="settings-actions">
                <button
                  className="tp-button"
                  disabled={busy !== null || config.programChatIds.length === 0}
                  onClick={() => void handleTest('TELEGRAM', 'PROGRAM')}
                >
                  {busy === 'test-program-telegram' ? 'Envoi…' : 'Tester le Telegram éditorial'}
                </button>
              </div>
            </div>

            <div className="settings-recipients__column">
              <label className="settings-option-row settings-option-row--selectable">
                <input
                  type="checkbox"
                  checked={config.programEmailEnabled}
                  onChange={event => patch({ programEmailEnabled: event.target.checked })}
                />
                <span className="settings-option-row__copy">
                  <span className="settings-option-row__title">Doubler par e-mail</span>
                </span>
              </label>

              <RecipientListField
                label="Adresses e-mail" kind="email"
                placeholder={'redaction@exemple.fr\nchef-edition@exemple.fr'}
                value={config.programEmailTo}
                onChange={setProgramEmailTo}
                hint="Serveur SMTP commun, réglé en bas de page."
              />

              <div className="settings-actions">
                <button
                  className="tp-button"
                  disabled={busy !== null || !config.smtpHost || config.programEmailTo.length === 0}
                  onClick={() => void handleTest('EMAIL', 'PROGRAM')}
                >
                  {busy === 'test-program-email' ? 'Envoi…' : 'Tester l’e-mail éditorial'}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* ── Rapports journaliers : sous-partie du flux éditorial ──────────────
            Un récapitulatif planifié, et non une réaction à un événement : il a
            son interrupteur et ses destinataires, indépendants des notifications
            éditoriales ci-dessus. */}
        <div className="settings-subgroup" role="group" aria-labelledby="notif-reports-title">
          <div className="settings-subgroup__heading">
            <span className="settings-subgroup__icon" aria-hidden="true"><CalendarClock size={16} /></span>
            <div className="settings-group__heading-copy">
              <h4 id="notif-reports-title" className="settings-subgroup__title">Rapports journaliers</h4>
              <p className="settings-group__description">
                En fin de journée, tout ce qui reste à faire avant que le lendemain parte à l’antenne, avec un
                bilan du jour et un aperçu succinct de la semaine. Envoyé même quand tout est prêt.
              </p>
            </div>
          </div>

          <div className="settings-section">
            <label className="settings-option-row settings-option-row--selectable">
              <input
                type="checkbox"
                checked={config.reportEnabled}
                onChange={event => patch({ reportEnabled: event.target.checked })}
              />
              <span className="settings-option-row__copy">
                <span className="settings-option-row__title">Envoyer le rapport journalier</span>
                <span className="settings-option-row__description">
                  Indépendant de l’interrupteur des notifications éditoriales.
                  {config.reportLastSentDay
                    ? ` Dernier rapport planifié envoyé le ${formatDay(config.reportLastSentDay)}.`
                    : ''}
                </span>
              </span>
            </label>
          </div>

          <div className="settings-section">
            <div className="settings-section__heading">
              <h5 className="settings-section__title">Planification</h5>
            </div>
            <div className="settings-report-schedule">
              <label>
                <span>Heure d’envoi</span>
                <input
                  className="tp-input" type="time" step={60} required
                  value={config.reportTime}
                  onChange={event => { if (event.target.value) patch({ reportTime: event.target.value.slice(0, 5) }) }}
                />
              </label>
              <fieldset className="settings-report-days">
                <legend>Jours d’envoi</legend>
                <div className="settings-report-days__list">
                  {REPORT_DAYS.map(day => (
                    <label key={day.value} className="settings-report-days__day" title={day.label}>
                      <input
                        type="checkbox"
                        checked={config.reportDays.includes(day.value)}
                        disabled={config.reportDays.length === 1 && config.reportDays.includes(day.value)}
                        onChange={event => toggleReportDay(day.value, event.target.checked)}
                        aria-label={day.label}
                      />
                      <span aria-hidden="true">{day.short}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>
            <p className="settings-section__description">
              Heure d’antenne. Le rapport d’un jour coché décrit le <strong>lendemain</strong> : cochez le
              dimanche pour préparer le lundi. Au redémarrage de l’API après l’heure, un rapport manqué part
              le soir même, jamais le lendemain.
            </p>
          </div>

          <div className="settings-section">
            <div className="settings-section__heading">
              <h5 className="settings-section__title">Contenu</h5>
            </div>
            <dl className="settings-report-content">
              {REPORT_CONTENT.map(block => (
                <div key={block.title}>
                  <dt>{block.title}</dt>
                  <dd>{block.items}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="settings-section">
            <div className="settings-section__heading">
              <h5 className="settings-section__title">Destinataires du rapport</h5>
            </div>

            {/* Inverse du flux éditorial : ici l'e-mail est le canal principal,
                parce que le rapport est long ; Telegram en porte un condensé. */}
            <div className="settings-recipients">
              <div className="settings-recipients__column">
                <label className="settings-option-row settings-option-row--selectable settings-option-row--static">
                  <span className="settings-option-row__copy">
                    <span className="settings-option-row__title">E-mail — canal principal</span>
                  </span>
                </label>

                <RecipientListField
                  label="Adresses e-mail" kind="email"
                  placeholder={'redaction@exemple.fr\ndirection@exemple.fr'}
                  value={config.reportEmailTo}
                  onChange={setReportEmailTo}
                  hint="Chaque adresse reçoit le rapport complet."
                />
              </div>

              <div className="settings-recipients__column">
                <label className="settings-option-row settings-option-row--selectable">
                  <input
                    type="checkbox"
                    checked={config.reportTelegramEnabled}
                    onChange={event => patch({ reportTelegramEnabled: event.target.checked })}
                  />
                  <span className="settings-option-row__copy">
                    <span className="settings-option-row__title">Doubler par Telegram</span>
                  </span>
                </label>

                <RecipientListField
                  label="Conversations Telegram" kind="telegram"
                  placeholder={'-1009876543210'}
                  value={config.reportChatIds}
                  onChange={setReportChatIds}
                  hint={<>Version condensée. « Détecter les conversations » dans <strong>Transports</strong>, puis « Ajouter au rapport ».</>}
                />
              </div>
            </div>

            <div className="settings-actions">
              <button
                className="tp-button"
                disabled={busy !== null}
                onClick={() => void handleReportPreview()}
              >
                {busy === 'report-preview' ? 'Préparation…' : 'Aperçu du rapport'}
              </button>
              <button
                className="tp-button"
                disabled={busy !== null || dirty || !config.smtpHost || config.reportEmailTo.length === 0}
                onClick={() => void handleReportSend()}
              >
                {busy === 'report-send' ? 'Envoi…' : 'Envoyer maintenant'}
              </button>
            </div>
            <p className="settings-section__description">
              {dirty
                ? 'Enregistrez d’abord : l’envoi immédiat part aux destinataires enregistrés.'
                : 'L’envoi immédiat ne remplace pas celui du soir, qui partira quand même à l’heure réglée.'}
            </p>

            {reportPreview && (
              <div className="settings-report-preview">
                <div className="settings-report-preview__bar">
                  <strong>{reportPreview.subject}</strong>
                  <button className="tp-button" onClick={() => setReportPreview(null)}>Fermer l’aperçu</button>
                </div>
                <pre className="settings-report-preview__body">{reportPreview.text}</pre>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ══ 3. Transports communs ═════════════════════════════════════════════ */}
      <section className="settings-group settings-group--neutral" aria-labelledby="notif-transport-title">
        <header className="settings-group__heading">
          <span className="settings-group__icon" aria-hidden="true"><Send size={18} /></span>
          <div className="settings-group__heading-copy">
            <div className="settings-group__title-row">
              <h3 id="notif-transport-title" className="settings-group__title">Transports</h3>
              <span className="settings-group__tag">Commun aux deux flux</span>
            </div>
            <p className="settings-group__description">
              Serveur d’envoi, bot et silence de maintenance, partagés par tous les envois. Les
              destinataires, eux, se règlent dans chaque partie ci-dessus.
            </p>
          </div>
        </header>

        <div className="settings-section">
          <div className="settings-section__heading">
            <h4 className="settings-section__title">Serveur e-mail (SMTP)</h4>
            <p className="settings-section__description">
              Avec Gmail ou Office 365, utilisez un mot de passe d’application dédié plutôt que le mot de
              passe du compte.
            </p>
          </div>

          <div className="settings-redundancy__form-grid">
            <label>
              <span>Serveur SMTP</span>
              <input
                className="tp-input" placeholder="smtp.gmail.com"
                value={config.smtpHost ?? ''}
                onChange={event => patch({ smtpHost: event.target.value || null })}
              />
            </label>
            <label>
              <span>Port</span>
              <input
                className="tp-input" type="number" min={1} max={65535}
                value={config.smtpPort}
                onChange={event => patch({ smtpPort: Number(event.target.value) })}
              />
            </label>
            <label>
              <span>Identifiant</span>
              <input
                className="tp-input" autoComplete="off"
                value={config.smtpUser ?? ''}
                onChange={event => patch({ smtpUser: event.target.value || null })}
              />
            </label>
            <label>
              <span>Mot de passe</span>
              <input
                className="tp-input" type="password" autoComplete="new-password"
                placeholder={config.hasSmtpPassword ? '•••••••• (enregistré)' : 'Non renseigné'}
                value={smtpPassword}
                onChange={event => setSmtpPassword(event.target.value)}
              />
            </label>
            <label>
              <span>Expéditeur</span>
              <input
                className="tp-input" placeholder="regie@exemple.fr"
                value={config.emailFrom ?? ''}
                onChange={event => patch({ emailFrom: event.target.value || null })}
              />
            </label>
            <label className="settings-redundancy__checkbox">
              <input
                type="checkbox"
                checked={config.smtpSecure}
                onChange={event => patch({ smtpSecure: event.target.checked })}
              /> TLS implicite (port 465)
            </label>
          </div>
        </div>

        <div className="settings-section">
          <div className="settings-section__heading">
            <h4 className="settings-section__title">Bot Telegram</h4>
            <p className="settings-section__description">
              Créez un bot avec <strong>@BotFather</strong> pour obtenir le jeton, enregistrez-le, puis
              <strong> ajoutez le bot au groupe</strong> et envoyez-y <code>/start</code>. La détection
              relève ensuite l’identifiant directement auprès de Telegram, sans recopie.
            </p>
          </div>

          <label>
            <span>Jeton du bot</span>
            <input
              className="tp-input" type="password" autoComplete="new-password"
              placeholder={config.hasTelegramToken ? '•••••••• (enregistré)' : '123456789:AA...'}
              value={telegramToken}
              onChange={event => setTelegramToken(event.target.value)}
            />
          </label>

          {discovered !== null && discovered.length > 0 && (
            <ul className="settings-notification-chats">
              {discovered.map(chat => (
                <li key={chat.id}>
                  <span>{chat.title}</span>
                  <code>{chat.id}</code>
                  <span>{chat.type}</span>
                  <button
                    className="tp-button"
                    disabled={config.telegramChatIds.includes(chat.id)}
                    onClick={() => addChatId(chat.id, 'INCIDENT')}
                  >
                    {config.telegramChatIds.includes(chat.id) ? 'Déjà en technique' : 'Ajouter au technique'}
                  </button>
                  <button
                    className="tp-button"
                    disabled={config.programChatIds.includes(chat.id)}
                    onClick={() => addChatId(chat.id, 'PROGRAM')}
                  >
                    {config.programChatIds.includes(chat.id) ? 'Déjà en éditorial' : 'Ajouter à l’éditorial'}
                  </button>
                  <button
                    className="tp-button"
                    disabled={config.reportChatIds.includes(chat.id)}
                    onClick={() => addChatId(chat.id, 'REPORT')}
                  >
                    {config.reportChatIds.includes(chat.id) ? 'Déjà au rapport' : 'Ajouter au rapport'}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="settings-actions">
            <button
              className="tp-button"
              disabled={busy !== null || !config.hasTelegramToken}
              onClick={() => void handleDiscover()}
            >
              {busy === 'discover' ? 'Détection…' : 'Détecter les conversations'}
            </button>
          </div>
          {!config.hasTelegramToken && (
            <p className="settings-section__description">
              Enregistrez d’abord le jeton du bot pour activer la détection.
            </p>
          )}
        </div>

        <div className="settings-section">
          <div className="settings-section__heading">
            <h4 className="settings-section__title">Silence de maintenance</h4>
            <p className="settings-section__description">
              Suspend tous les envois, technique comme éditorial ; le rapport journalier attend la levée du
              silence. Le redémarrage propre des conteneurs pose
              déjà un silence automatique de 5 minutes : ce réglage sert aux interventions plus longues
              (mise à jour CasparCG, câblage, bascule réseau).
            </p>
          </div>

          <p className="settings-section__description">
            {config.maintenanceUntil
              ? `⏸ Silence actif jusqu’à ${new Date(config.maintenanceUntil).toLocaleTimeString('fr-FR')}.`
              : 'Aucun silence en cours — les notifications partent normalement.'}
          </p>

          <div className="settings-actions">
            {MAINTENANCE_CHOICES.map(minutes => (
              <button
                key={minutes}
                className="tp-button"
                disabled={busy !== null}
                onClick={() => void handleMaintenance(minutes)}
              >
                Silence {minutes} min
              </button>
            ))}
            <button
              className="tp-button"
              disabled={busy !== null || !config.maintenanceUntil}
              onClick={() => void handleMaintenance(0)}
            >
              Lever le silence
            </button>
          </div>
        </div>

        <div className="settings-section">
          <div className="settings-section__heading">
            <h4 className="settings-section__title">Derniers envois</h4>
            <p className="settings-section__description">
              Tous les envois confondus. Permet de vérifier qu’une notification est bien partie, et de lire
              la cause d’un échec.
            </p>
          </div>
          {deliveries.length === 0
            ? <p className="settings-section__description">Aucun envoi enregistré.</p>
            : (
              <ol className="settings-notification-history">
                {deliveries.map(delivery => (
                  <li key={delivery.id}>
                    <time>{new Date(delivery.createdAt).toLocaleString('fr-FR')}</time>
                    <span>{delivery.transport === 'EMAIL' ? 'E-mail' : 'Telegram'} → {delivery.target}</span>
                    <span>
                      {ALERT_TYPE_LABELS[delivery.alertType]
                        ?? PROGRAM_EVENT_LABELS[delivery.alertType]
                        ?? (delivery.alertType === 'DAILY_REPORT' ? DAILY_REPORT_DELIVERY_LABEL : null)
                        ?? (delivery.alertType === 'INFOSERVICE_ANNOUNCEMENTS' ? INFOSERVICE_ALERT_DELIVERY_LABEL : null)
                        ?? delivery.alertType}
                    </span>
                    <span>{delivery.status === 'SENT' ? '✓ Envoyé' : `⚠ Échec : ${delivery.error ?? 'cause inconnue'}`}</span>
                  </li>
                ))}
              </ol>
            )}
        </div>
      </section>
    </div>
  )
})

/** `2026-09-23` → `mer. 23/09`, sans passer par le fuseau du poste. */
function formatDay(dayKey: string): string {
  const [year, month, day] = dayKey.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))
  const weekday = date.toLocaleDateString('fr-FR', { weekday: 'short', timeZone: 'UTC' })
  return `${weekday} ${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`
}

